(() => {
  const ROOT_SELECTOR = '[data-bmh-soundscape-root]';

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainder = Math.floor(seconds % 60);
    return `${minutes}:${String(remainder).padStart(2, '0')}`;
  };

  const init = (root) => {
    if (!root || root.dataset.bmhSoundscapeReady === 'true') return;
    root.dataset.bmhSoundscapeReady = 'true';
    const lifecycle = new AbortController();
    const signal = lifecycle.signal;

    const player = root.querySelector('[data-soundscape-player]');
    const audio = root.querySelector('[data-soundscape-audio]');
    const title = root.querySelector('[data-soundscape-title]');
    const state = root.querySelector('[data-soundscape-state]');
    const toggle = root.querySelector('[data-soundscape-toggle]');
    const progress = root.querySelector('[data-soundscape-progress]');
    const current = root.querySelector('[data-soundscape-current]');
    const duration = root.querySelector('[data-soundscape-duration]');
    const sources = [...root.querySelectorAll('[data-soundscape-source]:not(:disabled)')];
    let active = null;
    let request = 0;
    const playError = () => {
      render();
      if (state) state.textContent = 'No se pudo reproducir. Intentá de nuevo.';
    };

    const render = () => {
      const playing = Boolean(audio && !audio.paused && !audio.ended);
      player?.classList.toggle('is-playing', playing);
      if (toggle) toggle.setAttribute('aria-label', playing ? 'Pausar sonido' : 'Reproducir sonido');
      if (state) state.textContent = playing ? 'Reproduciendo' : active ? 'En pausa' : 'Muestra real';
    };

    const select = async (button, autoplay = true) => {
      if (!audio || !button) return;
      const source = button.dataset.soundscapeSource;
      if (!source) return;
      const currentRequest = ++request;

      if (active !== button) {
        active?.classList.remove('is-active');
        active?.setAttribute('aria-pressed', 'false');
        active = button;
        active.classList.add('is-active');
        active.setAttribute('aria-pressed', 'true');
        audio.src = source;
        audio.load();
        if (title) title.textContent = button.dataset.soundscapeName || 'Sonido relajante';
        if (progress) {
          progress.value = '0';
          progress.style.setProperty('--soundscape-progress', '0%');
        }
        if (current) current.textContent = '0:00';
        if (duration) duration.textContent = '0:00';
      }

      if (autoplay) {
        document.dispatchEvent(new CustomEvent('bmh:soundscape-play', { detail: { audio } }));
        try {
          await audio.play();
          if (document.hidden || signal.aborted) audio.pause();
        } catch {
          if (currentRequest === request && !signal.aborted) playError();
        }
      }
    };

    sources.forEach((button) => {
      button.addEventListener('click', () => {
        if (active === button && audio && !audio.paused) audio.pause();
        else select(button, true);
      });
    });

    toggle?.addEventListener('click', () => {
      if (!audio) return;
      if (!active) {
        select(sources[0], true);
        return;
      }
      if (audio.paused) {
        document.dispatchEvent(new CustomEvent('bmh:soundscape-play', { detail: { audio } }));
        audio.play().then(() => { if (document.hidden || signal.aborted) audio.pause(); }).catch(playError);
      } else {
        audio.pause();
      }
    });

    audio?.addEventListener('play', render);
    audio?.addEventListener('pause', render);
    audio?.addEventListener('ended', render);
    audio?.addEventListener('loadedmetadata', () => {
      if (duration) duration.textContent = formatTime(audio.duration);
    });
    audio?.addEventListener('timeupdate', () => {
      const percent = Number.isFinite(audio.duration) && audio.duration > 0
        ? (audio.currentTime / audio.duration) * 100
        : 0;
      if (current) current.textContent = formatTime(audio.currentTime);
      if (progress) {
        progress.value = String(percent);
        progress.style.setProperty('--soundscape-progress', `${percent}%`);
      }
    });
    audio?.addEventListener('error', () => {
      if (state) state.textContent = 'Audio no disponible';
      player?.classList.remove('is-playing');
    });

    progress?.addEventListener('input', () => {
      if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
      audio.currentTime = (Number(progress.value) / 100) * audio.duration;
    });

    document.addEventListener('bmh:soundscape-play', (event) => {
      if (event.detail?.audio !== audio && audio && !audio.paused) audio.pause();
    }, { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden && audio && !audio.paused) audio.pause();
    }, { signal });

    let visibilityObserver = null;
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      request += 1; lifecycle.abort(); visibilityObserver?.disconnect(); audio?.pause();
    }, { signal });
    if (toggle && !sources.length) toggle.disabled = true;

    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        root.classList.add('is-visible');
        observer.disconnect();
      }, { threshold: .15 });
      visibilityObserver.observe(root);
    } else {
      root.classList.add('is-visible');
    }

    render();
  };

  const initWithin = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin());
  } else {
    initWithin();
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
})();

